/**
 * Bulk-import spec for the per-event Roster flow (email-only invites).
 *
 * Registered in the workbook registry: the canonical Planning Workbook
 * (`event-planner-templates.xlsx`) carries a dedicated `Roster` sheet
 * with a single `email` column. The Attendees dialog reads this sheet
 * directly when an organizer uploads the canonical workbook. Single-
 * column hand-rolled files keep working — the dialog falls back to
 * `worksheets[0]` and treats column 1 as the email column.
 *
 * Naming note: the per-event surface is called "Attendees" in the page
 * heading and route URL, but the sheet/file is called "Roster" because
 * that's the natural language for a list of people for a specific event.
 * The sidebar nav and the import dialog title both read "Roster".
 *
 * The post-validation classification (added / not found / already on
 * event) is event-context state and stays in the dialog — it's not a
 * row-level validation concern.
 */
import { invalid, pending, type ImportSpec, type ParsedRow } from "./importSpec";
import { EMAIL_PATTERN, normalize, normalizeEmailKey } from "./importTypes";

export interface RawAttendeeImportRow
  extends Record<string, string | undefined> {
  email?: string;
}

export interface ParsedAttendeeImportRow {
  email: string;
}

export const attendeeImportSpec: ImportSpec<
  RawAttendeeImportRow,
  ParsedAttendeeImportRow
> = {
  sheetName: "Roster",
  purpose:
    "Roster for this event — one email per row. Each email is matched server-side against the global users + contacts list. Distinct from the Users sheet, which adds people to the platform globally.",
  columns: [
    { key: "email", note: "required — the person's email address" },
  ],
  example: { email: "alice.park@example.com" },
  validate(raw, lineNumber) {
    const email = normalize(raw.email).toLowerCase();
    const base: Omit<ParsedRow<ParsedAttendeeImportRow>, "status" | "message"> =
      {
        lineNumber,
        data: { email },
      };
    if (!email) {
      return invalid(base, "Email is required");
    }
    if (!EMAIL_PATTERN.test(email)) {
      return invalid(base, "Invalid email format");
    }
    return pending(base);
  },
  dedupe(rows) {
    const seen = new Set<string>();
    return rows.map((row) => {
      if (row.status !== "pending") return row;
      const key = normalizeEmailKey(row.data.email);
      if (seen.has(key)) {
        return {
          ...row,
          status: "invalid" as const,
          message: "Duplicate email within input",
        };
      }
      seen.add(key);
      return row;
    });
  },
};
